/**
 * Admin Access Utility Functions
 *
 * Runtime utility functions for checking admin access levels.
 * These functions work with the AdminAccessLevel enum from adminNavigation.types.ts.
 */

import { AdminAccessLevel } from '../types/admin/adminNavigation.types';

/**
 * Check if user has admin access (isVerityAdmin OR isDeveloper OR isInitiallyDeveloper)
 *
 * isInitiallyDeveloper is included so that developers using the DB-precedence override
 * (testing as Read Only or Verity Admin) can still access the Access Management page
 * to manage their override. This acts as an escape hatch — it grants minimal admin access
 * (Access Management only) rather than full admin access to all admin features.
 *
 * @param isVerityAdmin - Whether user is a Verity Admin
 * @param isDeveloper - Whether user is a Developer
 * @param isInitiallyDeveloper - Whether user was originally a Developer (before DB override)
 * @returns Whether the user has admin access
 */
export const hasAdminAccess = (
  isVerityAdmin: boolean,
  isDeveloper: boolean,
  isInitiallyDeveloper: boolean = false
): boolean => {
  return isVerityAdmin || isDeveloper || isInitiallyDeveloper;
};

/**
 * Check if user has full admin access (isVerityAdmin OR isDeveloper only)
 *
 * Unlike hasAdminAccess, this does NOT include isInitiallyDeveloper.
 * Use this for features that should not be accessible during DB-override mode
 * (e.g., Scenario Snapshots).
 *
 * @param isVerityAdmin - Whether user is a Verity Admin
 * @param isDeveloper - Whether user is a Developer
 * @returns Whether the user has full admin access
 */
export const hasFullAdminAccess = (isVerityAdmin: boolean, isDeveloper: boolean): boolean => {
  return isVerityAdmin || isDeveloper;
};

/**
 * Check if user has developer access (isDeveloper only)
 * @param isDeveloper - Whether user is a Developer
 * @returns Whether the user has developer access
 */
export const hasDeveloperAccess = (isDeveloper: boolean): boolean => {
  return isDeveloper;
};

/**
 * Check if user has access to a specific access level
 * @param accessLevel - The required access level
 * @param isVerityAdmin - Whether user is a Verity Admin
 * @param isDeveloper - Whether user is a Developer
 * @param isInitiallyDeveloper - Whether user was originally a Developer (before DB override)
 * @returns Whether the user has the required access level
 */
export const hasAccessToLevel = (
  accessLevel: AdminAccessLevel,
  isVerityAdmin: boolean,
  isDeveloper: boolean,
  isInitiallyDeveloper: boolean = false
): boolean => {
  switch (accessLevel) {
    case AdminAccessLevel.ADMIN:
      return hasAdminAccess(isVerityAdmin, isDeveloper, isInitiallyDeveloper);
    case AdminAccessLevel.DEVELOPER:
      return hasDeveloperAccess(isDeveloper);
    default:
      return false;
  }
};
